import * as React from "react";
import type { AiToolsApi } from "workflowEditorV3/WorkflowEditorV3";

import type { InitialTab } from "components/workflows/workflow-editor-v3/WorkflowEditorV3View";
import { Session } from "services/ai-agent/AiAgentService";
import * as aiAgentSessionRepository from "services/ai-agent/aiAgentSessionRepository";

export interface ConversationManagementHandlers {
    disabled: boolean;
    onNewConversation: () => void;
    onLoadConversation: (sessionId: string) => void;
    onSessionDeleted: (sessionId: string) => void;
    onSessionRenamed: (sessionId: string, name: string) => void;
}

export interface AIPanelContextValue {
    isEnabled: boolean;
    currentViewTitle?: string;
    sectionTitle?: string;
    setCurrentViewDetails: (currentViewTitle: string | undefined, sectionTitle: string) => void;
    isAiPanelOpen: boolean;
    setIsAiPanelOpen: (isOpen: boolean) => void;
    workflowEditorTabs: InitialTab[] | undefined;
    openWorkflowEditor: (tabs: InitialTab[]) => void;
    closeWorkflowEditor: () => void;
    aiToolsApi: AiToolsApi | undefined;
    registerAiTools: (tools: AiToolsApi | undefined) => void;
    pendingPrompt: string | undefined;
    setPendingPrompt: (prompt: string | undefined) => void;
    isProcessingPrompt: boolean;
    setIsProcessingPrompt: (isProcessing: boolean) => void;
    sessions: Session[];
    setSessions: (sessions: Session[]) => void;
    activeSessionId: string | undefined;
    setActiveSessionId: (sessionId: string | undefined) => void;
    conversationManagementHandlers: ConversationManagementHandlers | undefined;
    registerConversationManagementHandlers: (handlers: ConversationManagementHandlers | undefined) => void;
}

interface AIProvider {
    isEnabled: boolean;
    children: React.ReactNode;
    sessions: Session[];
    setSessions: (sessions: Session[]) => void;
}

const AIPanelContext = React.createContext<AIPanelContextValue | undefined>(undefined);

export const AIPanelProvider = (props: AIProvider) => {
    const [currentViewTitle, setCurrentViewTitle] = React.useState<string | undefined>(undefined);
    const [sectionTitle, setSectionTitle] = React.useState<string | undefined>(undefined);
    const [isAiPanelOpen, setIsAiPanelOpen] = React.useState<boolean>(false);
    const [workflowEditorTabs, setWorkflowEditorTabs] = React.useState<InitialTab[] | undefined>(undefined);
    const [aiToolsApi, setAiToolsApi] = React.useState<AiToolsApi | undefined>(undefined);
    const [pendingPrompt, setPendingPrompt] = React.useState<string | undefined>(undefined);
    const [isProcessingPrompt, setIsProcessingPrompt] = React.useState<boolean>(false);
    const [activeSessionId, setActiveSessionIdState] = React.useState<string | undefined>(
        () => aiAgentSessionRepository.getSessionId() ?? undefined
    );
    const [conversationManagementHandlers, setConversationManagementHandlers] = React.useState<
        ConversationManagementHandlers | undefined
    >(undefined);

    const setActiveSessionId = (sessionId: string | undefined) => {
        setActiveSessionIdState(sessionId);
        if (sessionId) {
            aiAgentSessionRepository.setSessionId(sessionId);
        } else {
            aiAgentSessionRepository.clear();
        }
    };

    React.useEffect(() => {
        document.body.classList.toggle("ai-panel-visible", props.isEnabled);
        return () => {
            document.body.classList.remove("ai-panel-visible");
        };
    }, [props.isEnabled]);

    const setCurrentViewDetails = (viewTitle: string | undefined, section: string) => {
        setCurrentViewTitle(viewTitle);
        setSectionTitle(section);
    };

    const openWorkflowEditor = (tabs: InitialTab[]) => {
        setWorkflowEditorTabs(tabs);
        setIsAiPanelOpen(false);
    };

    return (
        <AIPanelContext.Provider
            value={{
                isEnabled: props.isEnabled,
                currentViewTitle,
                sectionTitle,
                setCurrentViewDetails,
                isAiPanelOpen,
                setIsAiPanelOpen,
                workflowEditorTabs,
                openWorkflowEditor,
                closeWorkflowEditor: () => setWorkflowEditorTabs(undefined),
                aiToolsApi,
                registerAiTools: setAiToolsApi,
                pendingPrompt,
                setPendingPrompt,
                isProcessingPrompt,
                setIsProcessingPrompt,
                sessions: props.sessions,
                setSessions: props.setSessions,
                activeSessionId,
                setActiveSessionId,
                conversationManagementHandlers,
                registerConversationManagementHandlers: setConversationManagementHandlers,
            }}
        >
            {props.children}
        </AIPanelContext.Provider>
    );
};

export const useAIPanelContext = (): AIPanelContextValue => {
    const context = React.useContext(AIPanelContext);
    if (context === undefined) {
        throw new Error("useAIPanelContext must be used within AIPanelProvider");
    }
    return context;
};
